import { Skeleton } from '../../../../components/Skeleton';
import { Tooltip } from '../../../../components/Tooltip';

/**
 * The axis a tile selects on.
 *
 * TWO of them, and the split is the point rather than an implementation detail. Out of stock and
 * Low are values of the **stock status** — one derived state per item, mutually exclusive — where
 * near expiry, stolen and the two lease alerts are **needs-attention** flags that co-occur freely:
 * an item can be near expiry and holding stolen units at once. One axis could not carry both.
 */
type AlertAxis = 'stock' | 'alert';

interface Tile {
  key: string;
  axis: AlertAxis;
  /** The facet VALUE on that axis, exactly as the server emits it. */
  value: string;
  label: string;
  /** Null where nothing can answer it yet — rendered as an em dash, never a zero. */
  count: number | null;
  /** Why it cannot be pressed, where the reason is about the PRODUCT rather than the query. */
  unavailableReason?: string;
}

/**
 * Which tiles each TAB draws.
 *
 * **Filed by what the kind can actually report**, which is the same test the tab's own columns
 * pass: a Lease Ending tile over a list of consumables is a control that can only ever read zero,
 * and a row of permanent zeroes is a row the reader learns to skip — which is the one thing an
 * alert strip must not become.
 *
 * Read down the columns rather than across:
 *
 * - **Out of stock / Low** belong to every tab that holds stock. Real Property holds none — its
 *   rows read `Not tracked` — so it has neither.
 * - **Near expiry / Expired** need an item that tracks expiry: a consumable lot, or a term
 *   licence. An owned machine can be set to track one, and the ALERT axis is still reachable
 *   from the filter rail for a reader who wants it — what this decides is only what is worth a
 *   standing tile.
 * - **Delayed return / Stolen** are facts about individually named UNITS, which is Equipment.
 * - **Leased & rented** needs something a lease can be recorded against — a unit, or a
 *   registered property. It is the Leased & Rented TAB, read as a tile: that tab came out on
 *   06 Oct 2026, and this and the `leased` column are between them what replaced it. Equipment
 *   carries it because that is where the mismatch shows — a catalog row saying Owned over one
 *   rented van.
 * - **Lease ending / Lease expired** read a unit's own lease end date, so they belong to the
 *   property register, where one row is one building and the date is the thing to act on.
 *   Equipment reaches them from the rail's own Needs-attention group.
 *
 * <b>`delayed-return` is drawn by no tab any more</b> and is reached from the rail. It left
 * Equipment when Leased & Rented did, to keep that tab at four tiles — the strip sizes itself
 * to what it draws, and a fifth would have wrapped.
 *
 * A tile the reader has TICKED is drawn whatever this says — see `tilesFor`. A hidden control
 * that is narrowing the list is the failure `useFacetVisibility` already refuses in the rail.
 */
const TILES_BY_SLICE: Record<string, string[]> = {
  consumables: ['out-of-stock', 'low', 'near-expiry', 'expired'],
  equipment: ['out-of-stock', 'low', 'leased', 'stolen'],
  digital: ['out-of-stock', 'low', 'near-expiry', 'expired'],
  'real-property': ['leased', 'lease-expiring', 'lease-expired'],
};

/**
 * Every tile there is, in the reference's own order.
 *
 * One definition, read by both the per-tab list and the ticked-anyway rule, so a tile cannot end
 * up with a different label or a different axis depending on which of the two drew it.
 */
function allTiles(
  alertCounts: Record<string, number>,
  stockCounts: Record<string, number>,
): Tile[] {
  return [
    /*
      THE TWO THAT READ THE STOCK AXIS, and they read it because that is the axis that actually
      carries them. They were pointed at `alert` — which emits neither value — so both read zero
      forever, and pressing one sent `alert=out-of-stock`, which matched nothing and emptied the
      table. Product call, 20 Sep 2026: point them at the facet that already counts and already
      filters, rather than adding two values to an axis whose own meaning is "and also".
    */
    { key: 'out-of-stock', axis: 'stock', value: 'OutOfStock', label: 'Out of Stock', count: stockCounts.OutOfStock ?? 0 },
    { key: 'low', axis: 'stock', value: 'Low', label: 'Low', count: stockCounts.Low ?? 0 },

    { key: 'near-expiry', axis: 'alert', value: 'near-expiry', label: 'Near Expiry', count: alertCounts['near-expiry'] ?? 0 },
    { key: 'expired', axis: 'alert', value: 'expired', label: 'Expired', count: alertCounts.expired ?? 0 },

    /*
      ANSWERABLE from 21 Sep 2026. This was the one tile with no source — a deliberate em dash
      saying the product could not answer rather than a zero claiming nothing was overdue — and
      the Issuances module gave it one: a lend carries an expected return date, which is the
      only thing in the product that can run late.

      It is a real count on a real facet now, so it presses like every other tile.
    */
    {
      key: 'delayed-return',
      axis: 'alert',
      value: 'delayed-return',
      label: 'Delayed Return',
      count: alertCounts['delayed-return'] ?? 0,
    },

    { key: 'stolen', axis: 'alert', value: 'stolen', label: 'Stolen', count: alertCounts.stolen ?? 0 },

    /*
      LEASED & RENTED — the TAB of that name, read as a tile. Product call, 06 Oct 2026, in the
      reader's own words: *remove the Leases & rented, instead make it a column and a card*.

      <b>It is the half of that tab a column cannot replace.</b> The `leased` column says which
      rows are rented; this says how many there are across the whole register and narrows the
      list to them in one press — which is what the tab did, from wherever the reader is
      standing rather than from a tab of its own.

      <b>It is a STATE, not something waiting to be done about</b>, which makes it the one tile
      here that is not an alert in the ordinary sense. It rides this axis because the axis is an
      OR of co-occurring flags — exactly how a reader asks for "everything leased, and also
      anything overdue" — and because TENURE is owned by the tab strip and cannot be pressed
      from a card. The server agrees in the direction that matters: `leased` is deliberately
      absent from `NeedsAttention`, so a rented van does not put a badge on a tab.
    */
    { key: 'leased', axis: 'alert', value: 'leased', label: 'Leased & Rented', count: alertCounts.leased ?? 0 },

    /*
      LEASE MONITORING — the two tiles this product adds to the reference's six.

      The reference watches leases on its property register, one date per building. Here the date
      is on the UNIT, so these count units rather than properties — and they are NOT
      tenure-scoped on the server, so an item whose catalog row says Owned still reports the one
      rented unit under it. That mismatch is exactly the row a reader needs to see, which is why
      the Equipment tab is not the only place they can be reached: the rail's own Needs-attention
      group still carries both.

      TWO tiles rather than one: there is still time to renew a lease ending soon and none to
      renew one that lapsed, so a single figure would hide the half nobody can act on.
    */
    { key: 'lease-expiring', axis: 'alert', value: 'lease-expiring', label: 'Lease Ending', count: alertCounts['lease-expiring'] ?? 0 },
    { key: 'lease-expired', axis: 'alert', value: 'lease-expired', label: 'Lease Expired', count: alertCounts['lease-expired'] ?? 0 },
  ];
}

/**
 * The ALERT TILES over the stock-levels table — the hospital's own six, plus two for leases.
 *
 * A tile is a **shortcut into a facet the rail already carries**, not a second filter: pressing
 * one selects that axis, and the rail and the tiles are two views of one selection exactly as
 * the folder tabs and the status facet are. A tile with its own state would let the two disagree
 * about what the list is showing.
 *
 * **They are FILED BY TAB**, drawing only what the active kind can report — see
 * {@link TILES_BY_SLICE}. A tile that can only ever read zero is a control the reader learns to
 * skip, which is the one thing an alert strip must not become.
 *
 * **`DELAYED RETURN` is the one tile with no source**, and it reads an em dash rather than a
 * zero. That distinction is the whole reason it is drawn at all: a zero would claim the
 * organization has nothing overdue, which is a fact about the stock; an em dash says the product
 * cannot answer yet, which is a fact about the product.
 */
export function StockLevelAlerts({
  alertCounts,
  stockCounts,
  alertSelected,
  stockSelected,
  sliceKey,
  hidden = [],
  loading,
  onSelect,
}: {
  /** `alert` facet counts, keyed as the server emits them. */
  alertCounts: Record<string, number>;
  /** `stock` facet counts — `StockStatus` names, which is what Out of Stock and Low read. */
  stockCounts: Record<string, number>;
  alertSelected: string[];
  stockSelected: string[];
  /** The tab the reader is on. Undefined draws every tile. */
  sliceKey?: string;
  /**
   * Tiles the reader has put away, by key — the settings cog's Cards section.
   *
   * <b>A TICKED tile overrules it</b>, exactly as it overrules the tab filing. The two rules
   * answer the same question and must not disagree about it.
   */
  hidden?: readonly string[];
  /** First answer in flight — the tiles skeleton their FIGURES and stay unpressable. */
  loading: boolean;
  /** Replaces one axis's selection. Never a per-value toggle — the shell owns the state. */
  onSelect: (axis: AlertAxis, values: string[]) => void;
}) {
  const tiles = tilesFor(
    allTiles(alertCounts, stockCounts),
    sliceKey,
    { stock: stockSelected, alert: alertSelected },
    hidden,
  );

  if (tiles.length === 0) return null;

  return (
    // Sized to what is actually drawn: four tiles read as one row, and the two-tile tabs would
    // otherwise leave half a row of nothing beside them.
    <div className={`grid grid-cols-2 gap-3 ${tiles.length <= 2 ? 'sm:grid-cols-2' : 'sm:grid-cols-4'}`}>
      {tiles.map((tile) => {
        const selected = tile.axis === 'stock' ? stockSelected : alertSelected;
        const active = selected.includes(tile.value);

        /*
          THREE ways a tile is unpressable, and they are different facts:

            no SOURCE     the product cannot answer it yet — `unavailableReason` says which
                          module it waits on
            no FIGURE     the count is still in flight; selecting against a number nobody has
                          yet is a guess
            ZERO          nothing on this tab is in that state, so pressing it would narrow the
                          list to no rows at all

          The third is new — 28 Sep 2026 — and it is the dead-affordance rule reaching the
          strip: a control that can only ever empty the table is one the reader presses once
          and learns nothing from.

          <b>A TICKED tile stays pressable whatever it reads</b>, and that half is not
          optional: unticking is the only way back out of a selection, so a tile disabled
          while active would strand the reader on a list they cannot widen. It is the same
          reason a ticked tile is drawn on a tab that would not otherwise offer it.
        */
        const emptied = tile.count === 0 && !active;
        const unavailable = tile.count === null || loading || emptied;

        // The reason names WHICH of the three, or a disabled control says nothing at all.
        const reason =
          tile.unavailableReason ??
          (emptied ? `Nothing on this tab is ${tile.label.toLowerCase()}.` : null);

        const button = (
          <button
            type="button"
            disabled={unavailable}
            aria-pressed={active}
            onClick={() =>
              onSelect(
                tile.axis,
                active
                  ? selected.filter((v) => v !== tile.value)
                  : [...selected, tile.value],
              )
            }
            className={[
              'flex w-full items-center justify-between rounded-lg border px-4 py-3 text-left transition',
              unavailable
                ? 'cursor-not-allowed border-slate-200 bg-slate-50 text-slate-400'
                : active
                  ? 'border-slate-400 bg-blue-50 text-slate-900'
                  : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50',
            ].join(' ')}
          >
            <span className="truncate text-xs">{tile.label}</span>

            {/*
              The FIGURE, or a skeleton of it. `tabular-nums` so a row of tiles keeps its figures
              on one baseline, and the count is the darkest thing on the tile because it is what
              the reader scans for.
            */}
            {loading ? (
              <Skeleton className="ml-3 h-4 w-6 shrink-0" />
            ) : (
              <span className="ml-3 shrink-0 font-mono text-sm tabular-nums">
                {tile.count === null ? '—' : tile.count}
              </span>
            )}
          </button>
        );

        /*
          A disabled control SAYS WHY, and only where there is something to say: the reason is
          about the product having no source, not about the query being in flight. Wrapped only
          when it applies, because a tooltip renders a portal and a strip of them costs that on
          every tile for nothing.
        */
        return reason && !loading ? (
          <Tooltip key={tile.key} block disabledReason={reason}>
            {button}
          </Tooltip>
        ) : (
          <div key={tile.key}>{button}</div>
        );
      })}
    </div>
  );
}

/**
 * The tiles a tab draws — its own, plus any the reader has TICKED.
 *
 * The second half is the rule the filter rail already runs on and is not optional: a selection
 * the reader cannot see is a list narrowed by a control that is not on screen. A tile selected on
 * Consumables and then carried onto Real Property by a tab change stays drawn until it is
 * unticked, which is what makes it possible to untick.
 *
 * An unknown slice key draws everything, which is the honest fallback: a tab this map has not
 * been told about is better served by too many tiles than by none.
 */
function tilesFor(
  tiles: Tile[],
  sliceKey: string | undefined,
  selected: Record<AlertAxis, string[]>,
  hidden: readonly string[],
): Tile[] {
  const allowed = sliceKey === undefined ? undefined : TILES_BY_SLICE[sliceKey];

  return tiles.filter((tile) => {
    /*
      A TICKED TILE IS DRAWN WHATEVER EITHER RULE SAYS, and it is the same override twice.

      The tab filing and the reader's own put-away list are both about which tiles are worth
      STANDING there; neither decides what may be filtered. A selection the reader cannot see is
      a list narrowed by a control that is not on screen — which also makes it impossible to
      untick, since the only way back is the tile itself.
    */
    if (selected[tile.axis].includes(tile.value)) return true;

    // PUT AWAY by the reader — the settings cog's Cards section, persisted per module.
    if (hidden.includes(tile.key)) return false;

    // Filed by TAB. An unknown slice draws everything, which is the honest fallback.
    return !allowed || allowed.includes(tile.key);
  });
}
